import React, { useState } from 'react';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Route, ArrowLeft } from 'lucide-react';
import { RouteSidebarBody } from './RouteSidebar';

/**
 * Mobile-only bottom sheet for the Route Planner sidebar.
 * On mobile the map is the primary view; users tap the FAB to open the sheet,
 * pick destinations / review stops, then dismiss it and see the map full-screen.
 */
const RouteSidebarSheet = ({ selectedStops, navigate, ...bodyProps }) => {
  const [open, setOpen] = useState(false);
  const stopCount = selectedStops?.length || 0;

  return (
    <>
      {/* Floating back button — top-left corner (matches iOS/Android map app
          convention). Sits BELOW the top nav (h-16). Icon-only to save space
          and keep the zoom controls fully visible below. */}
      <Button
        variant="secondary"
        size="icon"
        onClick={() => navigate('/discovery')}
        className="md:hidden fixed top-20 left-3 z-[900] shadow-md h-11 w-11 rounded-full"
        data-testid="route-back-btn-mobile"
        aria-label="Back to discovery"
      >
        <ArrowLeft className="h-5 w-5" />
      </Button>

      {/* Floating trigger — bottom-right on mobile, above the bottom nav bar */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button
            className="md:hidden fixed bottom-24 right-4 z-[900] flex items-center gap-2 bg-primary text-primary-foreground rounded-full pl-4 pr-5 py-3 shadow-lg hover:bg-primary/90 transition-colors"
            data-testid="open-route-sidebar-mobile"
            aria-label="Open route planner controls"
          >
            <Route className="h-5 w-5" />
            <span className="font-medium text-sm">Plan</span>
            {stopCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] px-1.5">
                {stopCount}
              </Badge>
            )}
          </button>
        </SheetTrigger>

        <SheetContent
          side="bottom"
          className="p-0 h-[85vh] flex flex-col rounded-t-2xl"
          data-testid="route-sidebar-sheet"
        >
          <SheetHeader className="p-4 pb-2">
            <SheetTitle className="text-left flex items-center gap-2">
              <Route className="h-5 w-5 text-primary" />
              Route Planner
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto flex flex-col">
            <RouteSidebarBody
              {...bodyProps}
              navigate={navigate}
              selectedStops={selectedStops}
              hideBackButton
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default RouteSidebarSheet;
